import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 职业技能与实操技术 — 数据库索引原理
 *
 * 暗深色技术风 + 终端窗口 + B+ 树可视化 + 全表扫描 vs 索引扫描动效对比
 * 叙事弧：场景化痛点 → 全表扫描慢演示 → B+ 树结构 → 索引查询路径 → 性能对比 → 最佳实践
 * 尺寸：1920 × 1080
 */

interface BPlusNode {
  keys: string[];
  isLeaf?: boolean;
  highlight?: boolean;
}

interface BPlusLevel {
  level: number;
  nodes: BPlusNode[];
}

interface BestPractice {
  rule: string;
  reason: string;
  good: string;
  bad: string;
}

interface DatabaseIndexExpertProps {
  topic: string;
  subtitle: string;
  scenarioStat: { rows: string; problemMs: string; solvedMs: string };
  scenarioDescription: string;
  fullScanQuery: string;
  indexedQuery: string;
  bplus: BPlusLevel[];
  searchPath: string[];
  metrics: { label: string; full: number; indexed: number; unit: string }[];
  bestPractices: BestPractice[];
  takeaway: string;
}

const DEFAULT_PROPS: DatabaseIndexExpertProps = {
  topic: '数据库索引：从全表扫描到毫秒响应',
  subtitle: 'How B+ Tree Index Speeds Up Your Queries',
  scenarioStat: {
    rows: '1.2 亿',
    problemMs: '12,500 ms',
    solvedMs: '3 ms',
  },
  scenarioDescription:
    '订单表 1.2 亿行，按 user_id 查询历史订单。无索引：全表扫描 12.5 秒；建索引后：B+ 树定位仅需 3 毫秒。',
  fullScanQuery: 'SELECT * FROM orders WHERE user_id = 88242;',
  indexedQuery: 'CREATE INDEX idx_user ON orders(user_id);',
  bplus: [
    {
      level: 0,
      nodes: [{ keys: ['50'], highlight: true }],
    },
    {
      level: 1,
      nodes: [
        { keys: ['10', '30'] },
        { keys: ['70', '90'], highlight: true },
      ],
    },
    {
      level: 2,
      nodes: [
        { keys: ['1', '5', '8'], isLeaf: true },
        { keys: ['12', '20', '25'], isLeaf: true },
        { keys: ['33', '40', '47'], isLeaf: true },
        { keys: ['52', '63', '68'], isLeaf: true },
        { keys: ['72', '78', '85'], isLeaf: true },
        { keys: ['88', '92', '99'], isLeaf: true, highlight: true },
      ],
    },
  ],
  searchPath: ['1️⃣ 根节点比较：88 > 50，向右', '2️⃣ 中间层：88 > 70，进入右分支', '3️⃣ 叶子节点：定位到 [88, 92, 99]，找到目标'],
  metrics: [
    { label: '查询时间', full: 12500, indexed: 3, unit: 'ms' },
    { label: '扫描行数', full: 120000000, indexed: 12, unit: '行' },
    { label: '磁盘 I/O', full: 1850000, indexed: 4, unit: '次' },
  ],
  bestPractices: [
    {
      rule: '高基数列优先建索引',
      reason: '基数越高，过滤效果越好；性别等低基数列建索引意义不大',
      good: 'idx(user_id), idx(order_no)',
      bad: 'idx(gender), idx(status)',
    },
    {
      rule: '遵循最左前缀原则',
      reason: '联合索引 (a,b,c) 仅在以 a 开头的查询条件下生效',
      good: 'WHERE a=? AND b=?',
      bad: 'WHERE b=? AND c=?',
    },
    {
      rule: '避免在索引列上做运算或函数',
      reason: '一旦使用函数/运算，B+ 树将失效，回退为全表扫描',
      good: 'WHERE user_id = 88242',
      bad: 'WHERE LEFT(name,3) = "abc"',
    },
    {
      rule: '覆盖索引消除回表',
      reason: '查询字段全部包含在索引中，无需回到主键索引取数据',
      good: 'SELECT id, user_id FROM orders ...',
      bad: 'SELECT * FROM orders ...',
    },
  ],
  takeaway: '索引不是魔法，而是用空间换时间的精妙数据结构——理解 B+ 树，就能写出快十万倍的 SQL。',
};

export const DatabaseIndexExpertTemplate: React.FC<
  Partial<DatabaseIndexExpertProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const scenarioStart = 30;
  const fullScanStart = 100;
  const treeStart = 200;
  const pathStart = 320;
  const metricsStart = 420;
  const practiceStart = 500;

  const scenarioOpacity = interpolate(frame, [scenarioStart, scenarioStart + 25], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const fullScanLine = Math.floor(
    interpolate(frame, [fullScanStart + 30, fullScanStart + 90], [0, 7], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    }),
  );

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 80% 10%, #1e293b 0%, #0f172a 60%, #020617 100%)',
        fontFamily: 'JetBrains Mono, "SF Mono", "Cascadia Code", monospace',
        color: '#e2e8f0',
        overflow: 'hidden',
      }}
    >
      {/* Faint grid */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(56,189,248,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.05) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          opacity: 0.4,
        }}
      />

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          left: 60,
          right: 60,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 16}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 26,
        }}
      >
        <div
          style={{
            padding: '10px 22px',
            background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
            borderRadius: 10,
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: '0.06em',
            color: '#0f172a',
          }}
        >
          ▣ DB OPTIMIZATION
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#f1f5f9',
            fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
      </div>

      {/* ===== Scenario stats ===== */}
      <div
        style={{
          position: 'absolute',
          top: 110,
          left: 60,
          right: 60,
          padding: '18px 28px',
          background: 'linear-gradient(135deg, rgba(220,38,38,0.12), rgba(34,197,94,0.08))',
          border: '1px solid rgba(148,163,184,0.2)',
          borderRadius: 14,
          opacity: scenarioOpacity,
          display: 'flex',
          alignItems: 'center',
          gap: 28,
        }}
      >
        <div
          style={{
            flex: 1,
            fontSize: 24,
            color: '#cbd5e1',
            lineHeight: 1.55,
            fontFamily: '-apple-system, "PingFang SC", sans-serif',
          }}
        >
          {p.scenarioDescription}
        </div>
        <div style={{ display: 'flex', gap: 18 }}>
          <div
            style={{
              padding: '10px 20px',
              background: 'rgba(220,38,38,0.18)',
              borderRadius: 12,
              borderLeft: '4px solid #ef4444',
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 22, color: '#fca5a5', fontFamily: '-apple-system, "PingFang SC", sans-serif', whiteSpace: 'nowrap' }}>无索引</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#fca5a5', whiteSpace: 'nowrap' }}>
              {p.scenarioStat.problemMs}
            </div>
          </div>
          <div
            style={{
              padding: '10px 20px',
              background: 'rgba(34,197,94,0.15)',
              borderRadius: 12,
              borderLeft: '4px solid #22c55e',
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 22, color: '#86efac', fontFamily: '-apple-system, "PingFang SC", sans-serif', whiteSpace: 'nowrap' }}>有索引</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#86efac', whiteSpace: 'nowrap' }}>
              {p.scenarioStat.solvedMs}
            </div>
          </div>
        </div>
      </div>

      {/* ===== Terminal: Full Scan ===== */}
      <div
        style={{
          position: 'absolute',
          top: 225,
          left: 60,
          width: 800,
          height: 380,
          borderRadius: 14,
          background: '#0a0f1c',
          border: '1px solid rgba(239,68,68,0.3)',
          overflow: 'hidden',
          boxShadow: '0 0 40px rgba(239,68,68,0.15)',
          opacity: interpolate(frame, [fullScanStart, fullScanStart + 25], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <div
          style={{
            background: '#1e293b',
            padding: '12px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderBottom: '1px solid rgba(148,163,184,0.15)',
          }}
        >
          <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#ef4444' }} />
          <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#f59e0b' }} />
          <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#22c55e' }} />
          <span
            style={{
              fontSize: 22,
              color: '#94a3b8',
              marginLeft: 16,
              fontFamily: '-apple-system, "PingFang SC", monospace',
            }}
          >
            mysql@orders ~ 全表扫描 (Full Scan)
          </span>
        </div>
        <div style={{ padding: '20px 24px', fontSize: 20, lineHeight: 1.7 }}>
          <div style={{ color: '#e2e8f0' }}>
            <span style={{ color: '#22c55e' }}>$</span> {p.fullScanQuery}
          </div>
          <div
            style={{
              marginTop: 16,
              fontFamily: 'monospace',
              fontSize: 20,
              color: '#fca5a5',
              minHeight: 180,
              lineHeight: 1.55,
            }}
          >
            {Array.from({ length: Math.max(0, fullScanLine) }).map((_, i) => (
              <div key={i}>
                row {((i + 1) * 8923441).toLocaleString()} ...
                {i === fullScanLine - 1 ? ' ⬣' : ' ✗'}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== B+ Tree visualization ===== */}
      <div
        style={{
          position: 'absolute',
          top: 240,
          left: 900,
          width: 960,
          height: 380,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            alignSelf: 'flex-start',
            display: 'flex',
            alignItems: 'baseline',
            gap: 16,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontSize: 22,
              color: '#94a3b8',
              letterSpacing: '0.08em',
            }}
          >
            ▣ B+ TREE INDEX STRUCTURE
          </div>
          <div
            style={{
              fontSize: 17,
              color: '#22c55e',
              fontFamily: 'monospace',
              background: 'rgba(34,197,94,0.1)',
              padding: '4px 12px',
              borderRadius: 6,
            }}
          >
            {p.indexedQuery}
          </div>
        </div>
        <svg width="900" height="320" viewBox="0 0 960 360">
          <defs>
            <filter id="nodeGlow">
              <feGaussianBlur stdDeviation="3" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {p.bplus.map((level, lvlIdx) => {
            const yPos = 30 + lvlIdx * 130;
            const nodeWidth = level.nodes[0].keys.length * 38 + 8;
            const totalWidth = level.nodes.length * nodeWidth + (level.nodes.length - 1) * 30;
            const startX = (960 - totalWidth) / 2;

            const lvlAppear = interpolate(
              frame,
              [treeStart + lvlIdx * 25, treeStart + 30 + lvlIdx * 25],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );

            // Calculate which nodes are highlighted by search progress
            const pathStep = Math.floor((frame - pathStart) / 30);
            const isPathActive =
              (lvlIdx === 0 && pathStep >= 0) ||
              (lvlIdx === 1 && pathStep >= 1) ||
              (lvlIdx === 2 && pathStep >= 2);

            return level.nodes.map((node, nIdx) => {
              const w = node.keys.length * 38 + 8;
              const x = startX + nIdx * (w + 30);
              const isHighlight = node.highlight && isPathActive;

              const baseColor = node.isLeaf ? '#22d3ee' : '#a78bfa';
              const fillColor = isHighlight ? '#fbbf24' : baseColor;

              return (
                <g
                  key={`${lvlIdx}-${nIdx}`}
                  opacity={lvlAppear}
                  transform={`translate(0, ${(1 - lvlAppear) * -20})`}
                >
                  <rect
                    x={x}
                    y={yPos}
                    width={w}
                    height={48}
                    rx={6}
                    fill={isHighlight ? `${fillColor}40` : 'rgba(15,23,42,0.85)'}
                    stroke={fillColor}
                    strokeWidth={isHighlight ? 3 : 1.5}
                    filter={isHighlight ? 'url(#nodeGlow)' : undefined}
                  />
                  {node.keys.map((k, kIdx) => (
                    <g key={kIdx}>
                      {kIdx > 0 && (
                        <line
                          x1={x + kIdx * 38 + 4}
                          y1={yPos + 6}
                          x2={x + kIdx * 38 + 4}
                          y2={yPos + 42}
                          stroke={fillColor}
                          strokeWidth="1"
                          opacity="0.5"
                        />
                      )}
                      <text
                        x={x + kIdx * 38 + 23}
                        y={yPos + 33}
                        textAnchor="middle"
                        fill={isHighlight ? '#fef3c7' : '#f1f5f9'}
                        fontSize="24"
                        fontWeight="700"
                      >
                        {k}
                      </text>
                    </g>
                  ))}
                  {/* Pointers downward */}
                  {!node.isLeaf && lvlIdx < p.bplus.length - 1 && (() => {
                    const nextLevel = p.bplus[lvlIdx + 1];
                    const nextNodeW = nextLevel.nodes[0].keys.length * 38 + 8;
                    const nextTotalW = nextLevel.nodes.length * nextNodeW + (nextLevel.nodes.length - 1) * 30;
                    const nextStartX = (960 - nextTotalW) / 2;
                    const childIndices: number[] = [];
                    if (lvlIdx === 0) {
                      childIndices.push(0, 1);
                    } else if (lvlIdx === 1) {
                      childIndices.push(nIdx * 3, nIdx * 3 + 1, nIdx * 3 + 2);
                    }
                    return childIndices
                      .filter((c) => c < nextLevel.nodes.length)
                      .map((c, idx) => {
                        const childX = nextStartX + c * (nextNodeW + 30) + nextNodeW / 2;
                        const parentX = x + (w / (childIndices.length + 1)) * (idx + 1);
                        return (
                          <line
                            key={`p-${lvlIdx}-${nIdx}-${c}`}
                            x1={parentX}
                            y1={yPos + 48}
                            x2={childX}
                            y2={yPos + 130}
                            stroke="rgba(167,139,250,0.4)"
                            strokeWidth="1.5"
                          />
                        );
                      });
                  })()}
                </g>
              );
            });
          })}

          {/* Leaf chain - linked list */}
          {(() => {
            const leafLvl = p.bplus[p.bplus.length - 1];
            const leafW = leafLvl.nodes[0].keys.length * 38 + 8;
            const leafTotalW = leafLvl.nodes.length * leafW + (leafLvl.nodes.length - 1) * 30;
            const leafStartX = (960 - leafTotalW) / 2;
            const yLeaf = 30 + (p.bplus.length - 1) * 130 + 48;
            return (
              <line
                x1={leafStartX}
                y1={yLeaf + 14}
                x2={leafStartX + leafTotalW}
                y2={yLeaf + 14}
                stroke="#22d3ee"
                strokeWidth="1.5"
                strokeDasharray="3 3"
                opacity={interpolate(
                  frame,
                  [treeStart + 80, treeStart + 110],
                  [0, 0.7],
                  { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                )}
              />
            );
          })()}

          {/* Target marker - 动态定位到高亮叶子节点 */}
          {frame > pathStart + 80 && (() => {
            const leafLvl = p.bplus[p.bplus.length - 1];
            const targetIdx = leafLvl.nodes.findIndex((n) => n.highlight);
            if (targetIdx < 0) return null;
            const leafW = leafLvl.nodes[0].keys.length * 38 + 8;
            const leafTotalW =
              leafLvl.nodes.length * leafW + (leafLvl.nodes.length - 1) * 30;
            const leafStartX = (960 - leafTotalW) / 2;
            const targetCx = leafStartX + targetIdx * (leafW + 30) + leafW / 2;
            const yLeafTop = 30 + (p.bplus.length - 1) * 130;
            const yLeafCenter = yLeafTop + 24;
            const pulse = Math.sin(frame * 0.15) * 3;
            return (
              <g>
                <ellipse
                  cx={targetCx}
                  cy={yLeafCenter}
                  rx={leafW / 2 + 10 + pulse}
                  ry={32 + pulse}
                  fill="none"
                  stroke="#fbbf24"
                  strokeWidth="3"
                />
                <text
                  x={targetCx}
                  y={yLeafTop - 12}
                  textAnchor="middle"
                  fill="#fbbf24"
                  fontSize="22"
                  fontWeight="800"
                >
                  目标 = 88
                </text>
              </g>
            );
          })()}
        </svg>
      </div>

      {/* ===== Performance comparison (full width, 3-column) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 620,
          left: 60,
          right: 60,
          padding: '18px 24px',
          background: 'rgba(15,23,42,0.7)',
          border: '1px solid rgba(56,189,248,0.25)',
          borderRadius: 14,
        }}
      >
        <div
          style={{
            fontSize: 22,
            color: '#94a3b8',
            letterSpacing: '0.08em',
            marginBottom: 14,
          }}
        >
          ▣ 性能对比 · Performance Delta
        </div>
        <div style={{ display: 'flex', gap: 22 }}>
          {p.metrics.map((m, i) => {
            const sIn = spring({
              frame: frame - (metricsStart + i * 14),
              fps,
              config: { damping: 16, stiffness: 110 },
            });
            const ratio = m.full > 0 ? Math.log10(m.full / Math.max(1, m.indexed)) : 0;
            const indexedBarPct = Math.max(4, 100 / Math.pow(10, ratio));
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  opacity: sIn,
                  transform: `translateY(${(1 - sIn) * 12}px)`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      color: '#e2e8f0',
                      fontWeight: 700,
                      fontFamily: '-apple-system, "PingFang SC", sans-serif',
                    }}
                  >
                    {m.label}
                  </span>
                  <span style={{ fontSize: 20, color: '#fbbf24', fontWeight: 800 }}>
                    快 {Math.pow(10, ratio).toFixed(0)} 倍
                  </span>
                </div>
                <div
                  style={{
                    height: 28,
                    background: 'linear-gradient(90deg, #ef4444, #b91c1c)',
                    borderRadius: 5,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 14,
                    fontSize: 18,
                    color: '#fff',
                    fontWeight: 700,
                    marginBottom: 6,
                  }}
                >
                  {m.full.toLocaleString()} {m.unit}
                </div>
                <div
                  style={{
                    width: `${indexedBarPct}%`,
                    minWidth: 110,
                    height: 28,
                    background: 'linear-gradient(90deg, #22c55e, #15803d)',
                    borderRadius: 5,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 14,
                    fontSize: 18,
                    color: '#fff',
                    fontWeight: 700,
                  }}
                >
                  {m.indexed.toLocaleString()} {m.unit}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Best Practices ===== */}
      <div
        style={{
          position: 'absolute',
          top: 810,
          left: 60,
          right: 60,
          padding: '18px 24px',
          background: 'linear-gradient(135deg, rgba(56,189,248,0.06), rgba(167,139,250,0.04))',
          border: '1px solid rgba(56,189,248,0.2)',
          borderRadius: 14,
        }}
      >
        <div
          style={{
            fontSize: 22,
            color: '#94a3b8',
            letterSpacing: '0.1em',
            marginBottom: 14,
          }}
        >
          ▣ 实战最佳实践
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {p.bestPractices.map((bp, i) => {
            const sIn = spring({
              frame: frame - (practiceStart + i * 12),
              fps,
              config: { damping: 16, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  padding: '14px 16px',
                  background: 'rgba(15,23,42,0.6)',
                  border: '1px solid rgba(148,163,184,0.15)',
                  borderRadius: 12,
                  opacity: sIn,
                  transform: `translateY(${(1 - sIn) * 12}px)`,
                  fontFamily: '-apple-system, "PingFang SC", sans-serif',
                }}
              >
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: '#22d3ee',
                    marginBottom: 12,
                    lineHeight: 1.35,
                  }}
                >
                  {i + 1}. {bp.rule}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    color: '#86efac',
                    fontFamily: 'monospace',
                    background: 'rgba(34,197,94,0.1)',
                    padding: '6px 10px',
                    borderRadius: 6,
                    marginBottom: 6,
                    lineHeight: 1.4,
                  }}
                >
                  ✓ {bp.good}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    color: '#fca5a5',
                    fontFamily: 'monospace',
                    background: 'rgba(239,68,68,0.1)',
                    padding: '6px 10px',
                    borderRadius: 6,
                    lineHeight: 1.4,
                  }}
                >
                  ✗ {bp.bad}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 660], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #22d3ee, #a78bfa, #fbbf24)',
        }}
      />
    </AbsoluteFill>
  );
};
